/**
 * Datadog RUM Service
 *
 * This service provides a centralized interface for initializing and interacting
 * with Datadog Real User Monitoring (RUM) SDK. It handles runtime configuration,
 * user tracking, error reporting, and custom action tracking.
 *
 * Features:
 * - Runtime configuration integration
 * - Singleton pattern (prevents double initialization)
 * - Graceful degradation (app works without Datadog)
 * - Security-conscious (masks sensitive data)
 * - Type-safe API
 *
 * @module services/datadog-rum
 */

import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { logApp } from "@/utils/logApp";
import { version } from "../../package.json";
import type { ResolvedConfig } from "../config";

/**
 * Datadog RUM Service
 * Singleton service for managing Datadog RUM SDK lifecycle
 */
export class DatadogRumService {
	/**
	 * Initialization state flag
	 * Prevents double initialization
	 */
	private static initialized = false;

	/**
	 * Initialization promise
	 * Used to track async initialization state
	 */
	private static initPromise: Promise<void> | null = null;

	/**
	 * Initialize Datadog RUM with runtime configuration
	 *
	 * @param config - Resolved configuration from runtime config system
	 * @returns Promise that resolves when initialization is complete
	 *
	 * @example
	 * ```typescript
	 * const config = await initializeConfig();
	 * await DatadogRumService.initialize(config);
	 * ```
	 */
	static async initialize(config: ResolvedConfig): Promise<void> {
		// Return existing initialization promise if already initializing
		if (DatadogRumService.initPromise) {
			console.log(
				"🐶 Datadog RUM: Initialization already in progress, waiting...",
			);
			return DatadogRumService.initPromise;
		}

		// Prevent double initialization
		if (DatadogRumService.initialized) {
			console.warn("🐶 Datadog RUM: Already initialized, skipping...");
			return Promise.resolve();
		}

		DatadogRumService.initPromise =
			DatadogRumService._performInitialization(config);
		return DatadogRumService.initPromise;
	}

	/**
	 * Internal initialization logic
	 * @private
	 */
	private static async _performInitialization(
		config: ResolvedConfig,
	): Promise<void> {
		try {
			const rumConfig = config.integrations?.datadog?.rum;
			// console.log("config!:", config);

			// Check if Datadog RUM configuration exists
			if (!rumConfig) {
				console.log(
					"🐶 Datadog RUM: No configuration found, skipping initialization",
				);
				return;
			}

			// Check if enabled
			if (!rumConfig.enabled) {
				console.log("🐶 Datadog RUM: Disabled in configuration, skipping");
				return;
			}

			// Validate required credentials
			if (!rumConfig.applicationId || !rumConfig.clientToken) {
				console.warn(
					"🐶 Datadog RUM: Missing required credentials (applicationId or clientToken), skipping initialization",
				);
				return;
			}

			console.log("🐶 Datadog RUM: Initializing...");
			console.log(
				`   📊 Environment: ${rumConfig.env || config.app.environment}`,
			);
			console.log(`   🌐 Site: ${rumConfig.site || "us5.datadoghq.com"}`);
			console.log(`   🎯 Service: ${rumConfig.service || "fawkes-web"}`);
			console.log(
				`   🔑 Application ID: ${rumConfig.applicationId.substring(0, 8)}...`,
			);
			console.log(`   🔑 Version: ${version}`);

			// Initialize the Datadog RUM SDK
			datadogRum.init({
				applicationId: rumConfig.applicationId,
				clientToken: rumConfig.clientToken,
				site: (rumConfig.site || "us5.datadoghq.com") as "us5.datadoghq.com",
				service: rumConfig.service || "fawkes-web",
				env: rumConfig.env || config.app.environment,
				version: version || rumConfig.version || config.app.version,

				// Sampling rates
				sessionSampleRate: rumConfig.sessionSampleRate ?? 100,
				sessionReplaySampleRate: rumConfig.sessionReplaySampleRate ?? 100,

				// Tracking options
				trackUserInteractions: rumConfig.trackUserInteractions !== false,
				trackResources: rumConfig.trackResources !== false,
				trackLongTasks: rumConfig.trackLongTasks !== false,

				// Privacy settings
				defaultPrivacyLevel: rumConfig.defaultPrivacyLevel || "mask-user-input",

				// Enable automatic tracking
				trackViewsManually: false,
				// trackFrustrations: true,

				// Allow fallback to XHR
				allowFallbackToLocalStorage: true,

				// Compression
				useSecureSessionCookie: true,
				// useCrossSiteSessionCookie: false,
				plugins: [reactPlugin({ router: false })],
			});

			// Start session replay recording
			datadogRum.startSessionReplayRecording();

			DatadogRumService.initialized = true;
			console.log("✅ Datadog RUM: Initialized successfully");
		} catch (error) {
			console.error("❌ Datadog RUM: Initialization failed:", error);
			logApp({
				level: "error",
				message: "Datadog RUM initialization failed",
			});
			// Don't throw - observability failure shouldn't break the app
		} finally {
			DatadogRumService.initPromise = null;
		}
	}

	/**
	 * Check if Datadog RUM is initialized
	 *
	 * @returns True if initialized, false otherwise
	 */
	static isInitialized(): boolean {
		return DatadogRumService.initialized;
	}

	/**
	 * Set user information for tracking
	 *
	 * Associates the current session with a user for better debugging
	 * and user-specific analytics.
	 *
	 * @param userId - Unique user identifier
	 * @param email - User email (optional)
	 * @param name - User name (optional)
	 * @param additionalAttributes - Additional user attributes (optional)
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.addUser(
	 *   "user-123",
	 *   "user@example.com",
	 *   "John Doe",
	 *   { organization: "Acme Corp", role: "admin" }
	 * );
	 * ```
	 */
	static addUser(
		userId: string,
		email?: string,
		name?: string,
		additionalAttributes?: Record<string, unknown>,
	): void {
		if (!DatadogRumService.initialized) {
			console.debug("🐶 Datadog RUM: Not initialized, skipping addUser");
			return;
		}

		try {
			datadogRum.setUser({
				id: userId,
				email,
				name,
				...additionalAttributes,
			});

			console.log(`🐶 Datadog RUM: User set - ID: ${userId}`);
		} catch (error) {
			console.error("🐶 Datadog RUM: Failed to set user:", error);
			logApp({
				level: "error",
				message: "Datadog RUM failed to set user",
			});
		}
	}

	/**
	 * Remove user information
	 *
	 * Clears user identification from the current session.
	 * Useful for logout or privacy scenarios.
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.removeUser();
	 * ```
	 */
	static removeUser(): void {
		if (!DatadogRumService.initialized) {
			console.debug("🐶 Datadog RUM: Not initialized, skipping removeUser");
			return;
		}

		try {
			datadogRum.clearUser();
			console.log("🐶 Datadog RUM: User removed");
		} catch (error) {
			console.error("🐶 Datadog RUM: Failed to remove user:", error);
			logApp({
				level: "error",
				message: "Datadog RUM failed to remove user",
			});
		}
	}

	/**
	 * Add a custom error to Datadog
	 *
	 * Manually report errors that might not be caught automatically.
	 *
	 * @param error - Error object or error message
	 * @param context - Additional context about the error
	 *
	 * @example
	 * ```typescript
	 * try {
	 *   // Some operation
	 * } catch (error) {
	 *   DatadogRumService.addError(error, {
	 *     component: "UserProfile",
	 *     action: "saveProfile",
	 *     userId: currentUser.id
	 *   });
	 * }
	 * ```
	 */
	static addError(
		error: Error | string,
		context?: Record<string, unknown>,
	): void {
		if (!DatadogRumService.initialized) {
			console.debug("🐶 Datadog RUM: Not initialized, skipping addError");
			return;
		}

		try {
			if (typeof error === "string") {
				datadogRum.addError(new Error(error), context);
			} else {
				datadogRum.addError(error, context);
			}

			console.log("🐶 Datadog RUM: Error logged");
		} catch (err) {
			console.error("🐶 Datadog RUM: Failed to add error:", err);
			logApp({
				level: "error",
				message: "Datadog RUM failed to add error",
			});
		}
	}

	/**
	 * Add a custom action to Datadog
	 *
	 * Track custom user actions or business events.
	 *
	 * @param name - Name of the action
	 * @param context - Additional context about the action
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.addAction("purchase_completed", {
	 *   orderId: "order-123",
	 *   amount: 99.99,
	 *   currency: "USD"
	 * });
	 * ```
	 */
	static addAction(name: string, context?: Record<string, unknown>): void {
		if (!DatadogRumService.initialized) {
			console.debug("🐶 Datadog RUM: Not initialized, skipping addAction");
			return;
		}

		try {
			datadogRum.addAction(name, context);
			console.log(`🐶 Datadog RUM: Action logged - ${name}`);
		} catch (error) {
			console.error("🐶 Datadog RUM: Failed to add action:", error);
			logApp({
				level: "error",
				message: "Datadog RUM failed to add action",
			});
		}
	}

	/**
	 * Add feature flag evaluation
	 *
	 * Track feature flag evaluations for analytics.
	 *
	 * @param key - Feature flag key
	 * @param value - Feature flag value
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.addFeatureFlagEvaluation("new-checkout-flow", true);
	 * ```
	 */
	static addFeatureFlagEvaluation(key: string, value: unknown): void {
		if (!DatadogRumService.initialized) {
			console.debug(
				"🐶 Datadog RUM: Not initialized, skipping addFeatureFlagEvaluation",
			);
			return;
		}

		try {
			datadogRum.addFeatureFlagEvaluation(key, value);
			console.log(`🐶 Datadog RUM: Feature flag logged - ${key}: ${value}`);
		} catch (error) {
			console.error(
				"🐶 Datadog RUM: Failed to add feature flag evaluation:",
				error,
			);
			logApp({
				level: "error",
				message: "Datadog RUM failed to add feature flag evaluation",
			});
		}
	}

	/**
	 * Add timing to track performance
	 *
	 * Track custom performance timing.
	 *
	 * @param name - Name of the timing
	 * @param time - Time value (ms)
	 *
	 * @example
	 * ```typescript
	 * const startTime = performance.now();
	 * // ... some operation
	 * const duration = performance.now() - startTime;
	 * DatadogRumService.addTiming("data-fetch-duration", duration);
	 * ```
	 */
	static addTiming(name: string, time?: number): void {
		if (!DatadogRumService.initialized) {
			console.debug("🐶 Datadog RUM: Not initialized, skipping addTiming");
			return;
		}

		try {
			datadogRum.addTiming(name, time);
			console.log(`🐶 Datadog RUM: Timing logged - ${name}: ${time}ms`);
		} catch (error) {
			console.error("🐶 Datadog RUM: Failed to add timing:", error);
			logApp({
				level: "error",
				message: "Datadog RUM failed to add timing",
			});
		}
	}

	/**
	 * Set global context
	 *
	 * Add global context that will be attached to all events.
	 *
	 * @param key - Context key
	 * @param value - Context value
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.setGlobalContextProperty("tenant", "acme-corp");
	 * ```
	 */
	static setGlobalContextProperty(key: string, value: unknown): void {
		if (!DatadogRumService.initialized) {
			console.debug(
				"🐶 Datadog RUM: Not initialized, skipping setGlobalContextProperty",
			);
			return;
		}

		try {
			datadogRum.setGlobalContextProperty(key, value);
			console.log(`🐶 Datadog RUM: Global context set - ${key}`);
		} catch (error) {
			console.error(
				"🐶 Datadog RUM: Failed to set global context property:",
				error,
			);
			logApp({
				level: "error",
				message: "Datadog RUM failed to set global context property",
			});
		}
	}

	/**
	 * Remove global context property
	 *
	 * @param key - Context key to remove
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.removeGlobalContextProperty("tenant");
	 * ```
	 */
	static removeGlobalContextProperty(key: string): void {
		if (!DatadogRumService.initialized) {
			console.debug(
				"🐶 Datadog RUM: Not initialized, skipping removeGlobalContextProperty",
			);
			return;
		}

		try {
			datadogRum.removeGlobalContextProperty(key);
			console.log(`🐶 Datadog RUM: Global context removed - ${key}`);
		} catch (error) {
			console.error(
				"🐶 Datadog RUM: Failed to remove global context property:",
				error,
			);
			logApp({
				level: "error",
				message: "Datadog RUM failed to remove global context property",
			});
		}
	}

	/**
	 * Clear all global context
	 *
	 * @example
	 * ```typescript
	 * DatadogRumService.clearGlobalContext();
	 * ```
	 */
	static clearGlobalContext(): void {
		if (!DatadogRumService.initialized) {
			console.debug(
				"🐶 Datadog RUM: Not initialized, skipping clearGlobalContext",
			);
			return;
		}

		try {
			datadogRum.clearGlobalContext();
			console.log("🐶 Datadog RUM: Global context cleared");
		} catch (error) {
			console.error("🐶 Datadog RUM: Failed to clear global context:", error);
			logApp({
				level: "error",
				message: "Datadog RUM failed to clear global context",
			});
		}
	}
}

/**
 * Export singleton instance methods
 * For convenience and backward compatibility
 */
export const {
	initialize,
	isInitialized,
	addUser,
	removeUser,
	addError,
	addAction,
	addFeatureFlagEvaluation,
	addTiming,
	setGlobalContextProperty,
	removeGlobalContextProperty,
	clearGlobalContext,
} = DatadogRumService;
